import { CurrencyPipe, getCurrencySymbol } from '@angular/common'
import { ChangeDetectorRef, OnDestroy, Pipe, PipeTransform } from '@angular/core'
import { ApolloQueryResult } from '@apollo/client/core'
import { LocaleService } from 'framework/locale/services/locale.service'
import { TenantService } from 'framework/tenant/tenant.service'
import { TenantCurrencyGQL, TenantCurrencyQuery } from 'generated/graphql'
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs'
import { distinctUntilChanged, first, map, pluck, skip } from 'rxjs/operators'

type TenantCurrencyPipeInputValue = {
    value: string | number | null
    digitsInfo?: string
}

@Pipe({ name: 'tenantCurrency', pure: false })
export class TenantCurrencyPipe implements PipeTransform, OnDestroy {
    private returnValue: string = null

    private inputValue$: BehaviorSubject<TenantCurrencyPipeInputValue> = new BehaviorSubject(null)

    private currencyCode$ = this.tenantCurrencyGQL
        .fetch({ id: this.tenantService.tenant$.value })
        .pipe(
            first(),
            pluck<ApolloQueryResult<TenantCurrencyQuery>, TenantCurrencyQuery['TenantSettings']['currencyCode']>(
                'data',
                'TenantSettings',
                'currencyCode'
            )
        )

    private valueChangeDetectionSubscription: Subscription

    constructor(
        private currencyPipe: CurrencyPipe,
        private localeService: LocaleService,
        private tenantCurrencyGQL: TenantCurrencyGQL,
        private tenantService: TenantService,
        private changeDetectorRef: ChangeDetectorRef
    ) {
        this.valueChangeDetectionSubscription = combineLatest([
            this.currencyCode$,
            this.inputValue$.pipe(
                skip(1),
                distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
            ),
        ])
            .pipe(
                map<[TenantCurrencyQuery['TenantSettings']['currencyCode'], TenantCurrencyPipeInputValue], string>(
                    ([currencyCode, { value, digitsInfo }]) => {
                        const { locale } = this.localeService

                        if (value === null) {
                            return getCurrencySymbol(currencyCode, 'narrow', locale)
                        }

                        if (!digitsInfo) {
                            value = parseFloat(value.toString())
                            digitsInfo = Math.round(value) === value ? '1.0-0' : '1.2-2'
                        }

                        return this.currencyPipe.transform(value, currencyCode, 'symbol-narrow', digitsInfo, locale)
                    }
                )
            )
            .subscribe((value: string) => {
                this.returnValue = value
                this.changeDetectorRef.markForCheck()
            })
    }

    ngOnDestroy() {
        this.valueChangeDetectionSubscription.unsubscribe()
    }

    transform(value: string | number | null, digitsInfo?: string): string {
        this.inputValue$.next({
            value,
            digitsInfo,
        })

        return this.returnValue
    }
}
